class CentralSearch extends HTMLElement {
	constructor() {
		super();
		this.input = this.querySelector("[data-js-search-input]");
		this.clearButton = this.querySelector("[data-search-clear]");
		this.closeButton = this.querySelector("[data-search-close]");
		this.resultsContainer = this.querySelector("[data-js-search-results]");
		this.form = this.querySelector("form");
		this.cachedResults = {};
		this.cachedFallbackProducts = {};
		this.currentQuery = "";
		this.searchTimeout = null;
		this.isActive = false;

		this.init();
	}

	init() {
		// Gestion de l'input
		if (this.input) {
			this.input.addEventListener("input", this.handleInput.bind(this));
			this.input.addEventListener("focus", this.handleFocus.bind(this));
			this.input.addEventListener("keydown", this.handleKeydown.bind(this));
		}

		// Gestion du bouton clear (Effacer)
		if (this.clearButton) {
			this.clearButton.addEventListener("click", this.clearInput.bind(this));
		}

		// Gestion du bouton close (X)
		if (this.closeButton) {
			this.closeButton.addEventListener("click", this.closeSearch.bind(this));
		}

		// Gestion du clic à l'extérieur
		document.addEventListener("click", this.handleOutsideClick.bind(this));

		// Gestion de l'overlay
		this.createOverlay();
	}

	createOverlay() {
		this.overlay = document.createElement("div");
		this.overlay.className = "search-active-overlay";
		document.body.appendChild(this.overlay);
	}

	handleInput(e) {
		const query = e.target.value.trim();

		// Afficher/masquer les boutons selon l'état
		this.updateButtonsVisibility(query);

		// Gérer la recherche
		if (query !== this.currentQuery) {
			this.currentQuery = query;

			if (query.length >= 2) {
				this.performSearch(query);
			} else {
				this.hideResults();
			}
		}
	}

	async getFallbackProductsDocument(query) {
		if (this.cachedFallbackProducts[query]) {
			return this.cachedFallbackProducts[query];
		}

		const fallbackResponse = await fetch(
			`${KROWN.settings.routes.search_url}?q=${encodeURIComponent(query)}&type=product&options[prefix]=last&section_id=helper-central-search-products`,
		);

		if (!fallbackResponse.ok) {
			throw new Error("Fallback product search failed");
		}

		const fallbackText = await fallbackResponse.text();
		const fallbackDocument = new DOMParser().parseFromString(
			fallbackText,
			"text/html",
		);

		this.cachedFallbackProducts[query] = fallbackDocument;
		return fallbackDocument;
	}

	mergeFallbackProducts(resultsSection, fallbackDocument, targetProductCount) {
		const productGrid = resultsSection.querySelector(".search-products-grid");
		if (!productGrid) {
			return;
		}

		const existingCards = productGrid.querySelectorAll(".search-product-card");
		if (existingCards.length >= targetProductCount) {
			return;
		}

		const existingUrls = new Set(
			Array.from(existingCards)
				.map((card) => card.getAttribute("href"))
				.filter(Boolean),
		);

		const fallbackCards = fallbackDocument.querySelectorAll(".search-product-card");
		for (const fallbackCard of fallbackCards) {
			if (productGrid.querySelectorAll(".search-product-card").length >= targetProductCount) {
				break;
			}

			const fallbackUrl = fallbackCard.getAttribute("href");
			if (!fallbackUrl || existingUrls.has(fallbackUrl)) {
				continue;
			}

			existingUrls.add(fallbackUrl);
			productGrid.appendChild(fallbackCard.cloneNode(true));
		}
	}

	updateButtonsVisibility(query) {
		const hasQuery = query.length > 0;

		if (this.clearButton) {
			if (hasQuery && this.isActive) {
				this.clearButton.classList.add("show");
			} else {
				this.clearButton.classList.remove("show");
			}
		}

		if (this.closeButton) {
			if (this.isActive) {
				this.closeButton.classList.add("show");
			} else {
				this.closeButton.classList.remove("show");
			}
		}
	}

	handleFocus() {
		this.isActive = true;
		this.updateButtonsVisibility(this.input.value.trim());
		document.body.classList.add("search-active");

		if (this.currentQuery.length >= 2 && this.resultsContainer.innerHTML) {
			this.showResults();
		} else if (this.currentQuery.length === 0) {
			// Afficher les suggestions par défaut si le champ est vide
			this.loadDefaultSuggestions();
		}
	}

	handleKeydown(e) {
		if (e.key === "Escape") {
			this.closeSearch();
		}
	}

	handleOutsideClick(e) {
		if (
			!this.contains(e.target) &&
			!this.overlay.contains(e.target) &&
			this.isActive
		) {
			this.closeSearch();
		}
	}

	async loadDefaultSuggestions() {
		// Vérifier le cache pour les suggestions par défaut
		if (this.cachedResults["__default__"]) {
			this.renderResults(this.cachedResults["__default__"]);
			return;
		}

		// Afficher le loader
		this.showLoader();

		try {
			const response = await fetch(
				`${window.location.origin}/?section_id=helper-central-search-default`,
			);

			if (!response.ok) {
				throw new Error("Failed to load default suggestions");
			}

			const text = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(text, "text/html");
			const defaultSection = doc.querySelector(
				"#shopify-section-helper-central-search-default",
			);
			const defaultProductsGrid = defaultSection?.querySelector(
				".search-products-grid",
			);

			if (defaultProductsGrid) {
				const defaultProductCards = Array.from(
					defaultProductsGrid.querySelectorAll(".search-product-card"),
				);
				defaultProductCards.sort((a, b) => {
					const rankA = parseInt(a.dataset.bestSellerRank, 10) || Number.MAX_SAFE_INTEGER;
					const rankB = parseInt(b.dataset.bestSellerRank, 10) || Number.MAX_SAFE_INTEGER;
					return rankA - rankB;
				});
				const topCards = defaultProductCards.slice(0, 24);
				defaultProductsGrid.innerHTML = "";
				topCards.forEach((card) => {
					defaultProductsGrid.appendChild(card);
				});
			}
			const results =
				doc.querySelector("#shopify-section-helper-central-search-default")
					?.innerHTML || "";

			// Mettre en cache
			this.cachedResults["__default__"] = results;

			this.renderResults(results);
		} catch (error) {
			console.error("Default suggestions error:", error);
			this.hideResults();
		}
	}

	async performSearch(query) {
		// Vérifier le cache
		if (this.cachedResults[query]) {
			this.renderResults(this.cachedResults[query]);
			return;
		}

		// Afficher le loader
		this.showLoader();

		try {
			const response = await fetch(
				`${KROWN.settings.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,query,collection,page,article&resources[limit]=16&section_id=helper-central-search`,
			);

			if (!response.ok) {
				throw new Error("Search failed");
			}

			const text = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(text, "text/html");
			const resultsSection = doc.querySelector(
				"#shopify-section-helper-central-search",
			);
			const renderedProductCount =
				resultsSection?.querySelectorAll(".search-product-card").length || 0;

			if (resultsSection && renderedProductCount < 16) {
				const fallbackDocument = await this.getFallbackProductsDocument(query);
				this.mergeFallbackProducts(resultsSection, fallbackDocument, 16);
			}

			const results = resultsSection?.innerHTML || "";

			// Mettre en cache
			this.cachedResults[query] = results;

			this.renderResults(results);
		} catch (error) {
			console.error("Search error:", error);
			this.hideResults();
		}
	}

	renderResults(html) {
		if (!html.trim()) {
			this.hideResults();
			return;
		}

		this.resultsContainer.innerHTML = html;
		this.showResults();
	}

	showResults() {
		this.resultsContainer.style.display = "block";
		this.resultsContainer.classList.add("show");
		this.isActive = true;
		document.body.classList.add("search-active");
		this.updateButtonsVisibility(this.input.value.trim());

		// Ajouter les écouteurs d'événements aux résultats
		this.addResultListeners();
	}

	hideResults() {
		this.resultsContainer.style.display = "none";
		this.resultsContainer.classList.remove("show");
	}

	showLoader() {
		this.resultsContainer.innerHTML =
			KROWN.settings.predictive_search_placeholder ||
			'<div class="search-loading">Recherche en cours...</div>';
		this.showResults();
	}

	// Bouton "Effacer" - vide l'input mais garde le mode actif
	clearInput() {
		this.input.value = "";
		this.currentQuery = "";
		this.hideResults();
		this.updateButtonsVisibility("");
		this.input.focus();
	}

	// Bouton "X" - ferme complètement la recherche
	closeSearch() {
		this.input.value = "";
		this.currentQuery = "";
		this.hideResults();
		this.isActive = false;
		document.body.classList.remove("search-active");
		this.updateButtonsVisibility("");
		this.input.blur();
	}

	addResultListeners() {
		const items = this.resultsContainer.querySelectorAll(
			"[data-js-search-item]",
		);

		items.forEach((item) => {
			item.addEventListener("click", (e) => {
				const href =
					item.tagName === "A" ? item.href : item.querySelector("a")?.href;
				if (href) {
					window.location.href = href;
				}
			});
		});
	}

	// Gestion de la navigation au clavier dans les résultats
	handleResultNavigation(e) {
		const items = this.resultsContainer.querySelectorAll(
			"[data-js-search-item]",
		);
		let currentIndex = -1;

		const updateActiveItem = (index) => {
			items.forEach((item, i) => {
				item.classList.toggle("active", i === index);
			});

			if (index >= 0 && items[index]) {
				items[index].scrollIntoView({ block: "nearest" });
			}
		};

		if (e.key === "ArrowDown") {
			e.preventDefault();
			currentIndex = Math.min(currentIndex + 1, items.length - 1);
			updateActiveItem(currentIndex);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			currentIndex = Math.max(currentIndex - 1, -1);
			updateActiveItem(currentIndex);
		} else if (e.key === "Enter" && currentIndex >= 0) {
			e.preventDefault();
			items[currentIndex].click();
		}
	}
}

// Enregistrement du composant
if (typeof customElements.get("central-search") === "undefined") {
	customElements.define("central-search", CentralSearch);
}

// Extension pour gérer la navigation au clavier
document.addEventListener("DOMContentLoaded", () => {
	const centralSearch = document.querySelector("central-search");
	if (centralSearch) {
		centralSearch.input.addEventListener(
			"keydown",
			centralSearch.handleResultNavigation.bind(centralSearch),
		);
	}
});
